import { ContentBlock } from "draft-js";
import { FC, useState, useEffect } from "react";

interface AtomicYouTubeProps {
  block?: ContentBlock;
  contentState?: any;
  videoId?: string;
}

interface VideoMeta {
  title: string;
  channelTitle: string;
  description: string;
}

export const AtomicYouTube: FC<AtomicYouTubeProps> = ({
  block,
  contentState,
  videoId: propVideoId,
}) => {
  let videoId = propVideoId;

  if (!videoId && block && contentState) {
    const key = block.getEntityAt(0);
    if (key) {
      const entity = contentState.getEntity(key);
      const url: string = entity.getData().url;
      if (url) {
        const match = url.match(/(?:v=|youtu\.be\/)([\w-]+)/);
        videoId = match ? match[1] : undefined;
      }
    }
  }

  if (!videoId) return null;

  const [meta, setMeta] = useState<VideoMeta | null>(null);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;
        const res = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&part=snippet&key=${API_KEY}`
        );
        const json = await res.json();
        if (json.items && json.items.length > 0) {
          const { title, channelTitle, description } = json.items[0].snippet;
          setMeta({ title, channelTitle, description });
        }
      } catch (err) {
        console.error("Error retrieving YouTube data:", err);
      }
    };
    fetchMeta();
  }, [videoId]);

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <div
      style={{
        display: "flex",
        border: "1px solid #e1e8ed",
        borderRadius: "16px",
        overflow: "hidden",
        cursor: "pointer",
        maxWidth: "516px",
        maxHeight: "131px",
        height: "131px",
        margin: "0 auto",
        textDecoration: "none",
      }}
    >
      <div style={{ position: "relative" }}>
        <img
          src={thumbnailUrl}
          alt={meta?.title || "YouTube thumbnail"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            maxHeight: "129px",
            maxWidth: "129px",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "59px",
            height: "59px",
            borderRadius: "50%",
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingLeft: "5px",
          }}
        >
          <svg width="20" height="26" viewBox="8 5 11 14" fill="white">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "8px 12px",
          flex: "1",
          backgroundColor: "#fff",
          gap: "4px",
          overflow: "hidden",
          fontFamily:
            'TwitterChirp, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        {meta ? (
          <>
            <div
              style={{
                fontSize: "15px",
                color: "#536471",
                fontWeight: 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxHeight: "20px",
              }}
            >
              youtube.com
            </div>

            <div
              style={{
                fontSize: "15px",
                fontWeight: 400,
                color: "#536471",
                display: "-webkit-box",
                WebkitLineClamp: "1",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                maxHeight: "20px",
              }}
            >
              {meta.title}
            </div>

            <div
              style={{
                fontSize: "15px",
                color: "#536471",
                fontWeight: 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxHeight: "20px",
              }}
            >
              {meta.channelTitle}
            </div>

            <div
              style={{
                fontSize: "15px",
                color: "#536471",
                fontWeight: 400,
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: "20px",
                maxHeight: "40px",
              }}
            >
              {meta.description}
            </div>
          </>
        ) : (
          <div style={{ fontSize: "12px", color: "#536471", marginBottom: "60px" }}>Loading...</div>
        )}
      </div>
    </div>
  );
};
